import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Layout from './Layout';
import { 
  ArrowLeft, 
  Upload, 
  Zap, 
  MapPin, 
  DollarSign, 
  Layers, 
  ShieldCheck, 
  Cpu, 
  CheckCircle2,
  Sparkles,
  AlertTriangle
} from 'lucide-react';
import { 
  createAdminProject, 
  updateAdminProject, 
  fetchAdminProjectDetail 
} from '../Services/userServices';

const ProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    customer: '',
    category: 'residential',
    tag: 'Residential Solar',
    location: '',
    capacity: '',
    savings: '',
    amount: '',
    startDate: new Date().toISOString().split('T')[0],
    completionDate: '',
    status: 'Completed',
    description: '',
    panels: 'Monocrystalline 540W Bi-facial',
    inverter: '15kW Three-Phase Hybrid Inverter',
    battery: '10kWh Lithium Battery Backup',
    roofType: 'RCC Flat Rooftop',
  });

  const [imageFile, setImageFile] = useState(null); // Actual image file
  const [imagePreview, setImagePreview] = useState(''); // Preview URL
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // 🔄 Fetch Project Details from Backend if Editing
  useEffect(() => {
    if (isEditing) {
      const loadProjectDetails = async () => {
        setIsLoading(true);
        const result = await fetchAdminProjectDetail(id);
        if (result.success && result.data) {
          const p = result.data;
          setFormData({
            title: p.title || '',
            customer: p.customer || '',
            category: p.category || 'residential',
            tag: p.tag || 'Residential Solar',
            location: p.location || '',
            capacity: p.capacity || '',
            savings: p.savings || '',
            amount: p.amount || '',
            startDate: p.start_date || '',
            completionDate: p.completion_date || '',
            status: p.status || 'Completed',
            description: p.description || '',
            panels: p.panels || '',
            inverter: p.inverter || '',
            battery: p.battery || '',
            roofType: p.roof_type || '',
          });
          setImagePreview(p.image || '');
        } else {
          setErrorMessage(result.error || 'Failed to load project details.');
        }
        setIsLoading(false);
      };
      loadProjectDetails();
    }
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    const categoryTags = {
      residential: 'Residential Estate',
      commercial: 'Commercial Corporate',
      industrial: 'Industrial Complex',
      agriculture: 'Agri-tech Grid',
    };
    setFormData((prev) => ({
      ...prev,
      category,
      tag: categoryTags[category] || 'Solar Installation',
    }));
  };

  // 📁 Handle File Upload (Standard - No Base64)
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    const result = isEditing 
      ? await updateAdminProject(id, formData, imageFile) 
      : await createAdminProject(formData, imageFile);

    if (result.success) {
      setSuccessMessage(isEditing ? 'Project updated successfully!' : 'New project created successfully!');
      setTimeout(() => {
        navigate('/admin/projects');
      }, 1200);
    } else {
      setErrorMessage(result.error || 'Something went wrong while saving.');
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8 pb-16">
        {/* TOP BAR / BREADCRUMBS */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/projects"
              className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <Link to="/admin/projects" className="hover:text-emerald-500 transition-colors">Projects</Link>
                <span>/</span>
                <span className="text-emerald-600 dark:text-emerald-400">{isEditing ? 'Edit Project' : 'New Project'}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-0.5">
                {isEditing ? 'Edit Project Details' : 'Create & Publish New Project'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/admin/projects"
              className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
            >
              Cancel
            </Link>
            <button
              type="submit"
              form="project-form"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl bg-[#10B981] hover:bg-emerald-600 text-white font-bold text-xs shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2 disabled:opacity-50 cursor-pointer"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              <span>{isEditing ? 'Update Project' : 'Publish Project'}</span>
            </button>
          </div>
        </div>

        {/* NOTIFICATION BANNERS */}
        {successMessage && (
          <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold text-sm flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5" />
            <span>{successMessage}</span>
          </div>
        )}

        {errorMessage && (
          <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-bold text-sm flex items-center gap-3">
            <AlertTriangle className="w-5 h-5" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form id="project-form" onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* MAIN FORM COLUMN (2 Cols) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* 1. PRIMARY DETAILS */}
            <div className="bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8 shadow-sm dark:shadow-xl space-y-5">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
                <Layers className="w-5 h-5 text-[#10B981]" />
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Basic Project Information</h2>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Project Title / Name *</label>
                <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. The Sky-Villa Grid" required
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Customer / Client Name *</label>
                  <input type="text" name="customer" value={formData.customer} onChange={handleChange} placeholder="e.g. Rahul Sharma" required
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Sector / Category *</label>
                  <select name="category" value={formData.category} onChange={handleCategoryChange}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  >
                    <option value="residential">Residential Solar</option>
                    <option value="commercial">Commercial Solar</option>
                    <option value="industrial">Industrial Complex</option>
                    <option value="agriculture">Agricultural Agrivoltaics</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Location (City, State) *</label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                    <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Ahmedabad, Gujarat" required
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Capacity (kW / MW) *</label>
                  <div className="relative">
                    <Zap className="w-4 h-4 text-amber-500 absolute left-3.5 top-3.5" />
                    <input type="text" name="capacity" value={formData.capacity} onChange={handleChange} placeholder="e.g. 16.0 kW" required
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Project Description *</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows="4" required
                  placeholder="Describe the scope, client requirements, and outcome..."
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                />
              </div>
            </div>

            {/* 2. TECHNICAL SPECIFICATIONS */}
            <div className="bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8 shadow-sm dark:shadow-xl space-y-5">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
                <Cpu className="w-5 h-5 text-[#10B981]" />
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Technical & Equipment Specs</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">PV Panel Specifications</label>
                  <input type="text" name="panels" value={formData.panels} onChange={handleChange} placeholder="e.g. Monocrystalline 540W"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Inverter Model</label>
                  <input type="text" name="inverter" value={formData.inverter} onChange={handleChange} placeholder="e.g. 15kW Hybrid Inverter"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Battery Bank</label>
                  <input type="text" name="battery" value={formData.battery} onChange={handleChange} placeholder="e.g. 10kWh LiFePO4"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Roof Structure</label>
                  <input type="text" name="roofType" value={formData.roofType} onChange={handleChange} placeholder="e.g. RCC Elevated"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
              </div>
            </div>

            {/* 3. FINANCIALS & TIMELINE */}
            <div className="bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8 shadow-sm dark:shadow-xl space-y-5">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
                <DollarSign className="w-5 h-5 text-[#10B981]" />
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Financials & Timeline</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Annual Savings *</label>
                  <input type="text" name="savings" value={formData.savings} onChange={handleChange} placeholder="e.g. ₹1,80,000/yr" required
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Total Project Cost *</label>
                  <input type="text" name="amount" value={formData.amount} onChange={handleChange} placeholder="e.g. ₹8,50,000" required
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Start Date</label>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Completion Date</label>
                  <input type="date" name="completionDate" value={formData.completionDate} onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="space-y-6">
            
            {/* STATUS CARD */}
            <div className="bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm dark:shadow-xl space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white">Publishing Status</h3>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">Current Status</label>
                <select name="status" value={formData.status} onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                >
                  <option value="Completed">Completed & Live</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Pending">Pending Approval</option>
                </select>
              </div>
              <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                Completed projects will automatically appear in the <strong>Public Portfolio</strong>.
              </div>
            </div>

            {/* IMAGE UPLOAD */}
            <div className="bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm dark:shadow-xl space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white">Project Image</h3>
              
              {/* Upload Preview Container */}
              <label className="relative rounded-xl overflow-hidden aspect-[16/10] bg-slate-100 dark:bg-slate-800 border-2 border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center p-4 cursor-pointer hover:border-[#10B981] transition-all group">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="text-center text-slate-400 dark:text-slate-500">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-[#10B981] animate-bounce" />
                    <p className="text-xs font-bold">Upload Project Photo</p>
                    <p className="text-[10px] opacity-70 mt-1">PNG, JPG or WEBP</p>
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleImageFileChange} className="hidden" />
              </label>

              {/* {imagePreview && (
                <label className="flex flex-col items-center justify-center p-4 border border-slate-200 dark:border-slate-700 rounded-xl cursor-pointer hover:border-[#10B981] transition-colors bg-slate-50/50 dark:bg-slate-800/30">
                  <Upload className="w-5 h-5 text-[#10B981] mb-1" />
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Change Image</span>
                  <input type="file" accept="image/*" onChange={handleImageFileChange} className="hidden" />
                </label>
              )} */}
            </div>

            {/* Bottom Action */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/20 text-center space-y-3">
              <ShieldCheck className="w-6 h-6 text-[#10B981] mx-auto" />
              <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Ready to publish?</p>
              <button type="submit" form="project-form" disabled={isSubmitting}
                className="w-full py-3 bg-[#10B981] hover:bg-emerald-600 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 cursor-pointer"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                <span>{isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Publish to Portal'}</span>
              </button>
            </div>
          </div>

        </form>
      </div>
    </Layout>
  );
};

export default ProjectForm;